import { useEffect, useState } from 'react'
import { useGameStore } from '../stores/gameStore'

interface GameOverData {
  reason: 'time' | 'stability' | 'victory'
  timePlayed: number
  puzzlesSolved: number
  totalScore: number
  multiplier: number
  finalScore: number
  players: number
}

interface GameOverProps {
  data: GameOverData
}

// Simple confetti particle
interface Particle {
  id: number
  x: number
  y: number
  color: string
  rotation: number
  speed: number
}

function Confetti() {
  const [particles, setParticles] = useState<Particle[]>([])

  useEffect(() => {
    const colors = ['#00FF66', '#4488FF', '#FF8844', '#AA44FF', '#FFD700', '#FF69B4']
    const newParticles: Particle[] = []

    for (let i = 0; i < 50; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: -10 - Math.random() * 20,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        speed: 2 + Math.random() * 3,
      })
    }

    setParticles(newParticles)

    const interval = setInterval(() => {
      setParticles(prev =>
        prev.map(p => ({
          ...p,
          y: p.y + p.speed,
          rotation: p.rotation + 5,
        })).filter(p => p.y < 110)
      )
    }, 50)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute w-3 h-3"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            backgroundColor: p.color,
            transform: `rotate(${p.rotation}deg)`,
            borderRadius: Math.random() > 0.5 ? '50%' : '0',
          }}
        />
      ))}
    </div>
  )
}

export default function GameOver({ data }: GameOverProps) {
  const { leaveRoom } = useGameStore()
  const isVictory = data.reason === 'victory'

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getReasonText = (reason: string) => {
    switch (reason) {
      case 'time':
        return 'Czas minol!'
      case 'stability':
        return 'Stabilnosc spadla do zera!'
      case 'victory':
        return 'Plansza ukonczona!'
      default:
        return 'Koniec gry'
    }
  }

  const getSquadLabel = (players: number) => {
    switch (players) {
      case 1:
        return 'Solo'
      case 2:
        return 'Duo'
      case 3:
        return 'Trio'
      case 4:
        return 'Full Squad'
      default:
        return `${players} graczy`
    }
  }

  const handlePlayAgain = () => {
    // Leave room and go back to lobby to create a new game
    leaveRoom()
  }

  return (
    <div className={`fixed inset-0 flex items-center justify-center z-50 ${
      isVictory ? 'bg-emerald-900/90' : 'bg-black/90'
    }`}>
      {/* Confetti for victory */}
      {isVictory && <Confetti />}

      <div className={`relative bg-gray-800 rounded-xl p-8 max-w-md w-full mx-4 text-center ${
        isVictory ? 'border-4 border-emerald-neon shadow-lg shadow-emerald-neon/50' : 'border-2 border-gray-600'
      }`}>
        {/* Victory emoji */}
        {isVictory && (
          <div className="text-6xl mb-4 animate-bounce">🎉</div>
        )}

        {/* Header */}
        <h1 className={`text-3xl font-bold mb-2 ${
          isVictory ? 'text-emerald-neon' : 'text-red-400'
        }`}>
          {getReasonText(data.reason)}
        </h1>
        <p className="text-gray-400 mb-6">
          {isVictory ? 'Gratulacje! Wszystkie zagadki rozwiazane!' : 'Podsumowanie sesji'}
        </p>

        {/* Stats */}
        <div className="space-y-3 mb-8">
          {/* Time played */}
          <div className="flex justify-between items-center bg-gray-900 rounded-lg p-3">
            <span className="text-gray-400">Czas gry</span>
            <span className="text-xl font-mono text-white">{formatTime(data.timePlayed)}</span>
          </div>

          {/* Puzzles solved */}
          <div className="flex justify-between items-center bg-gray-900 rounded-lg p-3">
            <span className="text-gray-400">Rozwiazane zagadki</span>
            <span className="text-xl font-bold text-emerald-neon">{data.puzzlesSolved}</span>
          </div>

          {/* Squad */}
          <div className="flex justify-between items-center bg-gray-900 rounded-lg p-3">
            <span className="text-gray-400">Sklad druzyny</span>
            <span className="text-white">{getSquadLabel(data.players)}</span>
          </div>

          {/* Base score */}
          <div className="flex justify-between items-center bg-gray-900 rounded-lg p-3">
            <span className="text-gray-400">Punkty bazowe</span>
            <span className="text-white">{data.totalScore}</span>
          </div>

          {/* Multiplier */}
          <div className="flex justify-between items-center bg-gray-900 rounded-lg p-3">
            <span className="text-gray-400">Mnoznik ({getSquadLabel(data.players)})</span>
            <span className="text-yellow-400">x{data.multiplier.toFixed(1)}</span>
          </div>

          {/* Final score */}
          <div className={`flex justify-between items-center rounded-lg p-4 ${
            isVictory
              ? 'bg-emerald-neon/20 border-2 border-emerald-neon'
              : 'bg-emerald-neon/10 border border-emerald-neon'
          }`}>
            <span className="text-emerald-neon font-bold">Wynik koncowy</span>
            <span className="text-3xl font-bold text-yellow-400">{data.finalScore}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handlePlayAgain}
            className={`w-full px-6 py-3 font-bold rounded-lg transition-colors ${
              isVictory
                ? 'bg-emerald-neon text-gray-900 hover:bg-emerald-400'
                : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
          >
            {isVictory ? 'Zagraj ponownie' : 'Powrot do menu'}
          </button>

          {isVictory && (
            <button
              onClick={leaveRoom}
              className="w-full px-6 py-3 bg-gray-700 text-gray-300 font-bold rounded-lg
                         hover:bg-gray-600 transition-colors"
            >
              Powrot do lobby
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
